import React, { useState } from "react";
import Card from "components/card/Card";
import Page from "components/page/Page";
import { useAppSelector } from "hooks/redux";
import { IBusinessProfileForm, IProcessingFee } from "../teeOnPaymentsTypes";
import { TBankAccountExternal } from "redux/reducers/models/facility";
import { TTeeOnPaymentRepresentative } from "redux/reducers/models/transaction";
import { Badge } from "components/badge/Badge";
import "./accountDashboard.scss";
import MultipleActionDropdown from "components/multipleActionDropdown/MultipleActionDropdown";
import { t } from "i18next";
import BankAccountDisplay from "./BankAccountDisplay";
import DataTable from "components/dataTable/DataTable";
import { ButtonNew as Button } from "components/buttonNew";
import { capitalize } from "helpers/Helpers";
import { displayCurrency } from "helpers/currencyConverter";
import { useHistory } from "react-router";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import SelectNew from "components/select/SelectNew";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";

interface IAccountDashboardProps {
  businessProfile: IBusinessProfileForm & {
    activated?: boolean;
    stripeChargesEnabled?: boolean;
    stripePayoutsEnabled?: boolean;
  };
  bankAccounts: TBankAccountExternal[];
  representatives: TTeeOnPaymentRepresentative[];
  processingFees: IProcessingFee | undefined;
  onSaveRepresentativeChanges: (editRepresentativeState: IEditRepresentativeState) => Promise<boolean>;
}

export interface IEditRepresentativeState {
  editModalOpen: boolean;
  firstName: string;
  lastName: string;
  relationshipType: "director" | "executive" | "owner" | "representative" | null;
  email: string;
  representativeId: string;
}

export default function AccountDashboard(props: IAccountDashboardProps) {
  const { businessProfile, bankAccounts, representatives, processingFees, onSaveRepresentativeChanges } = props;
  const history = useHistory();

  const { authStore } = useAppSelector(store => store);
  const permissions = authStore.user?.permissions;

  const [editRepresentativeState, setEditRepresentativeState] = useState<IEditRepresentativeState>({
    editModalOpen: false,
    firstName: "",
    lastName: "",
    relationshipType: null,
    email: "",
    representativeId: "",
  });
  return (
    <Page
      title="Tee On Payments"
      subtitle="Manage and view information about your Tee On Payments integration"
      breadcrumbs={[
        {
          label: "Settings",
          url: "/admin/settings",
        },
        {
          label: "Payments",
          url: "/admin/settings/payments/general",
        },
        {
          label: "Tee On Payments",
          url: "/admin/settings/payments/tee-on-payments",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="flex items-center justify-between">
            <div className="flex space-md">
              <div className="flex items-center">
                <img
                  className="tee-on-payments-dashboard-account-logo-image"
                  src="/public/images/teeOnPayments/tee_on_payment_logo.jpg"
                  alt="icon.svg"
                />
              </div>

              <div className="flex flex-col space-sm">
                <div className="flex items-center  space-xxs">
                  {businessProfile.legalBusinessName ? (
                    <p className="text-xl text-semibold text-gray-900">{businessProfile?.legalBusinessName}</p>
                  ) : (
                    <>
                      <p className="text-sm text-regular text-gray-500">{t("Unnamed Business")}</p>{" "}
                      <div>
                        <OverlayTrigger
                          overlay={
                            <Tooltip id="input-tooltip" className="tooltip-background">
                              {t("Business name has not been added")}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faQuestionCircle} />
                        </OverlayTrigger>
                      </div>
                    </>
                  )}
                  <Badge size="medium" type={businessProfile?.activated ? "success" : "error"}>
                    {businessProfile?.activated ? "Enabled" : "Disabled"}
                  </Badge>
                </div>

                <div className="flex space-sm">
                  <div className="tee-on-payments-dashboard-payout-container">
                    <div
                      className="tee-on-payments-dashboard-payout-circle"
                      style={{
                        backgroundColor: businessProfile?.stripePayoutsEnabled ? "#12B76A" : "#F04438",
                      }}
                    ></div>
                    <p className="text-xs text-gray-700 text-medium">
                      {t(businessProfile?.stripePayoutsEnabled ? "Payouts enabled" : "Payouts disabled")}
                    </p>
                  </div>
                  <div className="tee-on-payments-dashboard-payout-container">
                    <div
                      className="tee-on-payments-dashboard-payout-circle"
                      style={{
                        backgroundColor: businessProfile?.stripeChargesEnabled ? "#12B76A" : "#F04438",
                      }}
                    ></div>
                    <p className="text-xs text-gray-700 text-medium">
                      {t(businessProfile?.stripeChargesEnabled ? "Payments enabled" : "Payments disabled")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end items-center">
              <MultipleActionDropdown
                alignment="right"
                options={[
                  {
                    type: "handler",
                    label: t("Edit account"),
                    handler: () => history.push("/admin/settings/payments/tee-on-payments/edit"),
                    icon: "pencil",
                  },
                ]}
                disabled={false}
              >
                <Button type="secondary" size="ellipsis">
                  <img src="/public/images/icons/verticalEllipsis.svg" alt="ellipsis.svg" />
                </Button>
              </MultipleActionDropdown>
            </div>
          </div>
        </Card.Section>
      </Card>

      <Card>
        <Card.Section>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg text-semibold text-gray-900">{t("Account status & payouts")}</p>
              <p className="text-sm text-regular text-subdued">
                {t("View your linked bank accounts and their status here.")}
              </p>
            </div>
          </div>

          <hr />

          <div className="mt-4">
            {bankAccounts.length === 0 ? (
              <p className="text-gray-500">{t("No payout accounts added")}</p>
            ) : (
              bankAccounts.map((bankAccount, index) => (
                <BankAccountDisplay
                  key={`${bankAccount.last_4}-${index}`}
                  accountNumber={bankAccount.last_4}
                  currency={bankAccount.currency}
                  bankAccountName={bankAccount.bank_name}
                  isDefault={bankAccount.currency_default}
                  manage
                  bankAccountId={bankAccount.id}
                />
              ))
            )}
          </div>
        </Card.Section>
      </Card>

      <Card
        title="Processing rates"
        subtitle="View accepted cards, sources and processing rates for domestic, premium and interac transactions."
      >
        <Card.Section>
          <div style={{ marginTop: "-24px" }}>
            <hr />
          </div>

          <div className="tee-on-payments-processing-rates-table mt-4">
            <div className="tee-on-payments-processing-rates-table-header">
              <div className="tee-on-payments-processing-rates-table-header-cell">{t("Category")}</div>
              <div className="tee-on-payments-processing-rates-table-header-cell">{t("Source")}</div>
              <div className="tee-on-payments-processing-rates-table-header-cell">{t("Rate")}</div>
            </div>

            <div className="tee-on-payments-processing-rates-table-row">
              <div className="tee-on-payments-processing-rates-table-category">
                <div className="tee-on-payments-processing-rates-table-category-title">{t("Domestic")}</div>
                <div className="tee-on-payments-processing-rates-table-category-icons">
                  <div>
                    <img src="/public/images/paymentMethods/visa.svg" alt="visa" />
                  </div>
                  <div>
                    <img src="/public/images/paymentMethods/mastercard.svg" alt="mastercard" />
                  </div>
                </div>
              </div>

              <div className="tee-on-payments-processing-rates-table-split-cell tee-on-payments-processing-rates-table-source-cell">
                <div className="tee-on-payments-processing-rates-table-split-row">{t("In-person")}</div>
                <div className="tee-on-payments-processing-rates-table-split-row">{t("Online")}</div>
              </div>

              <div className="tee-on-payments-processing-rates-table-split-cell tee-on-payments-processing-rates-table-rate-cell">
                <div className="tee-on-payments-processing-rates-table-split-row tee-on-payments-processing-rates-table-rate">
                  {((processingFees?.in_person_credit_rate ?? 0) * 100).toFixed(2)}% +{" "}
                  {displayCurrency("cad", processingFees?.in_person_credit_amount ?? 0)}
                </div>
                <div className="tee-on-payments-processing-rates-table-split-row tee-on-payments-processing-rates-table-rate">
                  {((processingFees?.online_credit_rate ?? 0) * 100).toFixed(2)}% +{" "}
                  {displayCurrency("cad", processingFees?.online_credit_amount ?? 0)}
                </div>
              </div>
            </div>

            <div className="tee-on-payments-processing-rates-table-row">
              <div className="tee-on-payments-processing-rates-table-category">
                <div className="tee-on-payments-processing-rates-table-category-title">{t("Premium")}</div>
                <div className="tee-on-payments-processing-rates-table-category-icons">
                  <div>
                    <img src="/public/images/paymentMethods/amex.svg" alt="amex" />
                  </div>
                  <div>
                    <img src="/public/images/paymentMethods/discover.svg" alt="discover" />
                  </div>
                </div>
              </div>

              <div className="tee-on-payments-processing-rates-table-split-cell tee-on-payments-processing-rates-table-source-cell">
                <div className="tee-on-payments-processing-rates-table-split-row">{t("In-person")}</div>
                <div className="tee-on-payments-processing-rates-table-split-row">{t("Online")}</div>
              </div>

              <div className="tee-on-payments-processing-rates-table-split-cell tee-on-payments-processing-rates-table-rate-cell">
                <div className="tee-on-payments-processing-rates-table-split-row tee-on-payments-processing-rates-table-rate">
                  {((processingFees?.in_person_premium_rate ?? 0) * 100).toFixed(2)}% +{" "}
                  {displayCurrency("cad", processingFees?.in_person_premium_amount ?? 0)}
                </div>
                <div className="tee-on-payments-processing-rates-table-split-row tee-on-payments-processing-rates-table-rate">
                  {((processingFees?.online_premium_rate ?? 0) * 100).toFixed(2)}% +{" "}
                  {displayCurrency("cad", processingFees?.online_premium_amount ?? 0)}
                </div>
              </div>
            </div>

            {/* only show interac if country is canada   */}
            {businessProfile?.country === "CA" && (
              <div className="tee-on-payments-processing-rates-table-row">
                <div className="tee-on-payments-processing-rates-table-category">
                  <div className="tee-on-payments-processing-rates-table-category-title">{t("Interac")}</div>
                  <div className="tee-on-payments-processing-rates-table-category-icons">
                    <div>
                      <img src="/public/images/paymentMethods/interac.svg" alt="interac" />
                    </div>
                  </div>
                </div>

                <div className="tee-on-payments-processing-rates-table-split-cell tee-on-payments-processing-rates-table-source-cell">
                  <div className="tee-on-payments-processing-rates-table-split-row">{t("In-person")}</div>
                  <div className="tee-on-payments-processing-rates-table-split-row">{t("Online")}</div>
                </div>

                <div className="tee-on-payments-processing-rates-table-split-cell tee-on-payments-processing-rates-table-rate-cell">
                  <div className="tee-on-payments-processing-rates-table-split-row tee-on-payments-processing-rates-table-rate">
                    {displayCurrency("cad", processingFees?.in_person_interac_amount ?? 0)}
                  </div>
                  <div className="tee-on-payments-processing-rates-table-split-row tee-on-payments-processing-rates-table-rate">
                    <em>{t("Not applicable")}</em>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card.Section>
      </Card>

      {permissions?.tee_on_payments_view_persons ? (
        <Card
          title="Representatives"
          subtitle="View the list of authorized representatives attached to your payment account."
        >
          <Card.Section>
            <div style={{ marginTop: "-24px" }}>
              <hr />
            </div>

            <DataTable
              columns={[
                { label: "Name" },
                { label: "Role" },
                { label: "Email", width: "20%" },
                { label: "Status", width: "20%" },
                // { label: "Action", width: "10%", justify: "center" },
              ]}
              loading={representatives === undefined}
              className="tee-on-payments-representatives-table min-h-auto mt-4"
            >
              {representatives.map((representative, index) => {
                const badgeType = () => {
                  if (representative.verification?.status === "verified") {
                    return "success";
                  }
                  if (representative.verification?.status === "unverified") {
                    return "error";
                  }
                  if (representative.verification?.status === "processing") {
                    return "warning";
                  }
                  if (representative.verification?.status === "pending") {
                    return "warning";
                  }

                  return "warning";
                };

                const relationshipType = Object.keys(representative.relationship).find(
                  key => representative.relationship[key as keyof typeof representative.relationship],
                );

                return (
                  <tr key={`${representative.email}-${index}`}>
                    <td className="text-sm text-gray-900 text-medium ">
                      <div className="flex items-center gap-2">
                        <div className="tee-on-payments-representatives-pill-avatar">
                          <span className="text-sm text-gray-600">
                            {representative.first_name.charAt(0)}
                            {representative.last_name.charAt(0)}
                          </span>
                        </div>

                        <p className="text-sm text-gray-900 text-medium">
                          {representative.first_name} {representative.last_name}
                        </p>
                      </div>
                    </td>
                    <td>{capitalize(relationshipType)}</td>
                    <td>{representative.email}</td>
                    <td>
                      <Badge type={badgeType()} size="small">
                        {capitalize(representative?.verification?.status)}
                      </Badge>
                    </td>
                    {/* <td>
                        <Button
                          type="text"
                          className="tee-on-payments-representatives-table-action-button"
                          onClick={() =>
                            setEditRepresentativeState({
                              ...editRepresentativeState,
                              editModalOpen: true,
                              firstName: representative.first_name ?? "",
                              lastName: representative.last_name ?? "",
                              relationshipType: relationshipType as "director" | "executive" | "owner" | "representative",
                              email: representative.email ?? "",
                              representativeId: representative.id,
                            })
                          }
                          size="ellipsis"
                        >
                          <img src="/public/images/icons/edit.svg" alt="edit.svg" />
                        </Button>
                      </td> */}
                  </tr>
                );
              })}
            </DataTable>
          </Card.Section>
        </Card>
      ) : null}

      <Portal isMounted={editRepresentativeState.editModalOpen}>
        <Sheet
          open={editRepresentativeState.editModalOpen}
          closable
          size="small"
          title={t("Add Representative")}
          onCancel={() =>
            setEditRepresentativeState({
              ...editRepresentativeState,
              editModalOpen: false,
              firstName: "",
              lastName: "",
              relationshipType: null,
              email: "",
              representativeId: "",
            })
          }
          cancelText={t("Cancel")}
          onOk={() => {
            void onSaveRepresentativeChanges(editRepresentativeState).then(success => {
              if (!success) {
                return;
              }

              setEditRepresentativeState({
                editModalOpen: false,
                firstName: "",
                lastName: "",
                relationshipType: null,
                email: "",
                representativeId: "",
              });
            });
          }}
          okText={t("Save changes")}
          overflow
          okDisabled={
            !editRepresentativeState.firstName ||
            !editRepresentativeState.lastName ||
            !editRepresentativeState.email ||
            !editRepresentativeState.relationshipType
          }
        >
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={editRepresentativeState.firstName}
                  label={t("First Name")}
                  id="first_name"
                  onChange={e => setEditRepresentativeState({ ...editRepresentativeState, firstName: e.target.value })}
                  placeholder={t("First Name")}
                  error={!editRepresentativeState.firstName}
                />
                <Input
                  value={editRepresentativeState.lastName}
                  label={t("Last Name")}
                  id="last_name"
                  onChange={e => setEditRepresentativeState({ ...editRepresentativeState, lastName: e.target.value })}
                  placeholder={t("Last Name")}
                  error={!editRepresentativeState.lastName}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <SelectNew
                  id="relationship_type"
                  label="Role"
                  value={editRepresentativeState.relationshipType}
                  onChange={(nextRelationshipType: "director" | "executive" | "owner" | "representative") =>
                    setEditRepresentativeState({ ...editRepresentativeState, relationshipType: nextRelationshipType })
                  }
                  options={[
                    { id: "director", label: "Director" },
                    { id: "executive", label: "Executive" },
                    { id: "owner", label: "Owner" },
                    { id: "representative", label: "Representative" },
                  ].map(relationship => ({
                    id: relationship.id,
                    label: relationship.label,
                  }))}
                  placeholder={t("Select role")}
                  error={!editRepresentativeState.relationshipType}
                />

                <Input
                  value={editRepresentativeState.email}
                  label={t("Email")}
                  id="email"
                  onChange={e => setEditRepresentativeState({ ...editRepresentativeState, email: e.target.value })}
                  placeholder={t("Email")}
                  error={!editRepresentativeState.email}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Sheet>
      </Portal>
    </Page>
  );
}
